import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStateMachine } from 'little-state-machine'
import { updateStore } from '../../../store'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid'
import { formatPrizeTypeLabel, INCENTIVE_BASIS_LABEL } from '../../../utils/incentiveDisplay'
import styles from './SearchCaddieCard.module.scss'

const getCaddieTeamLabel = (teamValue) => {
	if (!teamValue) return null
	const teamMap = {
		spoeum: '스포이음',
		voicecaddie: '보이스캐디',
		mansujung: '만수정',
		other: '기타',
		스포이음: '스포이음',
		보이스캐디: '보이스캐디',
		만수정: '만수정',
		기타: '기타',
	}
	return teamMap[teamValue] || teamValue
}

const formatCurrency = (amount) => {
	if (amount === null || amount === undefined) return '-'
	try {
		return `${Number(amount).toLocaleString('ko-KR')}원`
	} catch (_) {
		return `${amount}`
	}
}

const formatExpenseReimbursementPercent = (value) => {
	if (value === null || value === undefined) return '-'
	try {
		return `${Number(value)}%`
	} catch (_) {
		return `${value}`
	}
}

const formatYesNo = (val) => (val ? '가능' : '불가능')

const formatPayroll = (cycle) => {
	switch (cycle) {
		case 'weekly':
			return '주급'
		case 'monthly':
			return '월급'
		case 'perGame':
			return '경기당'
		default:
			return cycle || '-'
	}
}

const formatTaxIncluded = (included) => {
	if (included === undefined || included === null) return '-'
	return included ? '세후' : '세전'
}

const formatIncentiveRange = (startRank, endRank) => {
	if (startRank === 1 && endRank === 1) return '우승'
	const endLabel = endRank === 0 ? 'CUT' : endRank
	return `${startRank} ~ ${endLabel}`
}

const sortIncentives = (incentives) => {
	if (!Array.isArray(incentives) || incentives.length === 0) return []
	return [...incentives].sort((a, b) => {
		if (a.startRank !== b.startRank) return a.startRank - b.startRank
		const aEnd = a.endRank === 0 ? 9999 : a.endRank
		const bEnd = b.endRank === 0 ? 9999 : b.endRank
		return aEnd - bEnd
	})
}

const getCareerLevelLabel = (level) => {
	if (!level) return ''
	const map = {
		NEWBIE: '신입',
		ONE_TO_THREE: '1년 ~ 3년차',
		FOUR_TO_SIX: '4년 ~ 6년차',
		SEVEN_TO_TEN: '7년 ~ 10년차',
		OVER_TEN: '10년 이상',
	}
	return map[level] || level
}

const getTournamentRankLabel = (rank) => {
	const map = {
		FIRST: '우승',
		SECOND: 'TOP2',
		THIRD: 'TOP3',
		FOURTH: 'TOP4',
		FIFTH: 'TOP5',
		SIXTH: 'TOP6',
		SEVENTH: 'TOP7',
		EIGHTH: 'TOP8',
		NINTH: 'TOP9',
		TENTH: 'TOP10',
	}
	return map[rank] || rank
}

const getProficiencyLabel = (p) => {
	if (p === 'BEGINNER') return '기초회화 가능'
	if (p === 'INTERMEDIATE') return '원활한 의사소통 가능'
	if (p === 'ADVANCED') return '네이티브'
	return p || ''
}

export const SearchCaddieCard = ({
	caddie,
	attendanceLabel = null,
	selectionMode = false,
	selected = false,
	onSelect,
}) => {
	const [activeContractTab, setActiveContractTab] = useState('domestic')
	const [isDetailOpen, setIsDetailOpen] = useState(false)
	const navigate = useNavigate()
	const { actions, state } = useStateMachine({ updateStore })

	const preferred = caddie?.user_preferred_contract?.[0]
	const domestic = preferred?.domesticContract
	const international = preferred?.overseasContract
	const utc = caddie?.user_tour_caddie

	// 대표 경력 1개 - caddie_careers에서 is_representative인 것
	const representativeCareer = useMemo(() => {
		const list = utc?.caddie_careers || []
		return list.find((c) => c.is_representative === true) || null
	}, [utc?.caddie_careers])

	const domesticKeyValues = useMemo(() => {
		if (!domestic) return []
		return [
			{ key: '세금 포함 여부', value: formatTaxIncluded(domestic.isSalaryTaxIncluded) },
			{ key: '임금 지급 주기', value: formatPayroll(domestic.payrollCycles) },
			{ key: '컷 통과', value: formatCurrency(domestic.weeklySalaryCutThrough) },
			{ key: '컷 아웃', value: formatCurrency(domestic.weeklySalaryCutFail) },
			{ key: '제주 대회 참여', value: formatYesNo(domestic.jejuTournamentParticipation) },
			{ key: '제주 지원금', value: formatCurrency(domestic.expenseReimbursementJeju) },
			{ key: INCENTIVE_BASIS_LABEL, value: formatPrizeTypeLabel(domestic?.prizeType) },
			{
				key: '인센티브 금액',
				...(domestic?.incentives?.length > 0
					? {
							incentiveLines: sortIncentives(domestic.incentives).map(
								(i) =>
									`${formatIncentiveRange(i.startRank, i.endRank)} : ${i.percentage}%`,
							),
						}
					: { value: '-' }),
			},
			{ key: '스폰서십 모자', value: formatYesNo(domestic.wearSponsoredHat) },
		]
	}, [domestic])

	const internationalKeyValues = useMemo(() => {
		if (!international) return []
		return [
			{ key: '세금 포함 여부', value: formatTaxIncluded(international.isSalaryTaxIncluded) },
			{ key: '임금 지급 주기', value: formatPayroll(international.payrollCycles) },
			{ key: '컷 통과', value: formatCurrency(international.weeklySalaryCutThrough) },
			{ key: '컷 아웃', value: formatCurrency(international.weeklySalaryCutFail) },
			{ key: INCENTIVE_BASIS_LABEL, value: formatPrizeTypeLabel(international?.prizeType) },
			{
				key: '인센티브 금액',
				...(international?.incentives?.length > 0
					? {
							incentiveLines: sortIncentives(international.incentives).map(
								(i) =>
									`${formatIncentiveRange(i.startRank, i.endRank)} : ${i.percentage}%`,
							),
						}
					: { value: '-' }),
			},
			{ key: '스폰서십 모자', value: formatYesNo(international.wearSponsoredHat) },
			{
				key: '해외 지원금',
				value: formatExpenseReimbursementPercent(international.expenseReimbursement),
			},
		]
	}, [international])

	const handleOfferClick = () => {
		if (selectionMode && onSelect) {
			onSelect()
			return
		}
		actions.updateStore({
			offerInput: {
				...state.offerInput,
				targetUserId: caddie?.id,
				selectedCaddie: caddie,
			},
		})
		navigate('/search?tab=partner')
	}

	if (!caddie) return null

	const typeLabel =
		caddie.user_type === 'tourCaddie'
			? '투어 캐디'
			: caddie.user_type === 'houseCaddie'
				? '하우스'
				: ''
	const careerLevel = getCareerLevelLabel(utc?.career_level)
	const teamName = utc?.caddie_team_name
		? getCaddieTeamLabel(utc.caddie_team_name) || '무소속'
		: '무소속'
	const hasDomestic = domesticKeyValues.length > 0
	const hasInternational = internationalKeyValues.length > 0
	const careers = utc?.caddie_careers || []
	const languages = utc?.caddie_languages || []
	const selfIntro = utc?.self_introduction

	return (
		<div className={`${styles.card} ${selected ? styles.cardSelected : ''}`}>
			<div className={styles.imageWrap}>
				{careerLevel && <span className={styles.careerLevelBadge}>{careerLevel}</span>}
				<img
					src={caddie.profile_image || '/images/caddie-default-profile.svg'}
					alt={`${caddie.name} 프로필`}
					onError={(e) => {
						e.target.src = '/images/caddie-default-profile.svg'
					}}
				/>
			</div>

			<div className={styles.body}>
				<div className={styles.header}>
					<span className={styles.typeLabel}>{typeLabel}</span>
					<div className={styles.nameRow}>
						<h2 className={styles.name}>{caddie.name}</h2>
						{teamName && <span className={styles.teamName}>{teamName}</span>}
					</div>
					{/* 대표경력: 없어도 영역 유지해 카드 높이 통일 */}
					<div className={styles.representativeWrap}>
						<span className={styles.representativeLabel}>대표경력</span>
						{representativeCareer ? (
							<div className={styles.representativeRow}>
								<div className={styles.representativeLeft}>
									<span className={styles.representativeTournament}>
										{representativeCareer.tournament_name}
									</span>
									<span className={styles.representativeYear}>
										{representativeCareer.tournament_date
											? new Date(
													representativeCareer.tournament_date,
												).getFullYear() + '년'
											: ''}
									</span>
								</div>
								<span className={styles.representativeRank}>
									{getTournamentRankLabel(representativeCareer.tournament_rank)}
								</span>
							</div>
						) : (
							<p className={styles.representativePlaceholder}>
								아직 설정되지 않았습니다.
							</p>
						)}
					</div>
					{/* 소개: 접었을 때만 한 줄 + 계약조건(펼치기) 버튼, 펼치면 숨김 */}
					{!isDetailOpen && (
						<div className={styles.introWrap}>
							<span className={styles.introLabel}>소개</span>
							<p className={styles.introTextOneLine}>
								{selfIntro && selfIntro.trim()
									? selfIntro.trim()
									: '소개가 없습니다.'}
							</p>
							<button
								type="button"
								className={styles.detailToggle}
								onClick={() => setIsDetailOpen(!isDetailOpen)}
								aria-expanded={isDetailOpen}>
								계약조건
								<ChevronDownIcon className={styles.detailToggleIcon} />
							</button>
						</div>
					)}
					{/* 펼쳤을 때: 상세 접기 버튼만 표시 (소개 영역은 detailContent에서) */}
					{isDetailOpen && (
						<button
							type="button"
							className={styles.detailToggle}
							onClick={() => setIsDetailOpen(false)}
							aria-expanded={true}
							style={{ marginTop: 12 }}>
							접기
							<ChevronUpIcon className={styles.detailToggleIcon} />
						</button>
					)}
				</div>

				{isDetailOpen && (
					<div className={styles.detailContent}>
						{/* 상세 펼쳤을 때 소개 전문 */}
						{selfIntro && selfIntro.trim() && (
							<div className={styles.section}>
								<h3 className={styles.sectionTitle}>소개</h3>
								<p className={styles.selfIntro}>{selfIntro}</p>
							</div>
						)}
						{/* 계약 탭 */}
						{(hasDomestic || hasInternational) && (
							<div className={styles.section}>
								<h3 className={styles.sectionTitle}>선호계약정보</h3>
								<div className={styles.contractTabs}>
									{hasDomestic && (
										<button
											type="button"
											className={`${styles.contractTab} ${activeContractTab === 'domestic' ? '' : styles.contractTabInactive}`}
											onClick={() => setActiveContractTab('domestic')}>
											국내
										</button>
									)}
									{hasInternational && (
										<button
											type="button"
											className={`${styles.contractTab} ${activeContractTab === 'international' ? '' : styles.contractTabInactive}`}
											onClick={() => setActiveContractTab('international')}>
											해외
										</button>
									)}
								</div>
								<div className={styles.keyValues}>
									{(activeContractTab === 'domestic'
										? domesticKeyValues
										: internationalKeyValues
									).map((item, idx) => (
										<div
											key={idx}
											className={
												item.incentiveLines
													? styles.keyValueRowBlock
													: styles.keyValueRow
											}>
											<span className={styles.key}>{item.key}</span>
											{item.incentiveLines ? (
												<ul className={styles.incentiveList}>
													{item.incentiveLines.map((line, i) => (
														<li
															key={i}
															className={styles.incentiveListItem}>
															{line}
														</li>
													))}
												</ul>
											) : (
												<span className={styles.value}>{item.value}</span>
											)}
										</div>
									))}
								</div>
							</div>
						)}

						{/* 경력사항 - 상세 안 전체 대회 리스트 */}
						{careers.length > 0 && (
							<div className={styles.section}>
								<h3 className={styles.sectionTitle}>경력사항</h3>
								<ul className={styles.careerList}>
									{careers.map((career, idx) => (
										<li key={career.id || idx} className={styles.careerItem}>
											<div>
												<span className={styles.careerName}>
													{career.tournament_name}
												</span>
												<span className={styles.careerMeta}>
													{career.tournament_date
														? career.tournament_date.substring(0, 7)
														: ''}{' '}
													· {career.league || ''}
												</span>
											</div>
											<span className={styles.careerRank}>
												{getTournamentRankLabel(career.tournament_rank)}
											</span>
										</li>
									))}
								</ul>
							</div>
						)}

						{/* 외국어 사용능력 - 있을 때만 표시 */}
						{languages.length > 0 && (
							<div className={styles.section}>
								<h3 className={styles.sectionTitle}>외국어 사용능력</h3>
								<ul className={styles.langList}>
									{languages.map((lang, idx) => (
										<li key={lang.id || idx} className={styles.langItem}>
											<span className={styles.langName}>
												{lang.language_name}
											</span>
											<span className={styles.langLevel}>
												{getProficiencyLabel(lang.proficiency)}
											</span>
										</li>
									))}
								</ul>
							</div>
						)}
					</div>
				)}

				<button type="button" className={styles.offerButton} onClick={handleOfferClick}>
					{selectionMode ? (selected ? '선택됨' : '선택') : '제안하기'}
				</button>
			</div>
		</div>
	)
}

export default SearchCaddieCard
